import {
  SidePanelDS,
  Spacer,
  TextCapitalized,
  dateFormatterDayMonthYearLong2Digits,
} from "@qivia/ui";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { formatCurrency } from "../../../../../libSlice/materialUiFormats";
import { HomeChargingSupportingDocument } from "../../homeChargingSupportingDocumentsAPI";
import "../../../../../i18n/i18n";

export const SupportingDocumentDetails = (props: {
  supportingDocument: HomeChargingSupportingDocument;
}) => {
  const { t } = useTranslation();
  const { supportingDocument } = props;

  const createdAtValue = useMemo(() => {
    if (!supportingDocument.supportingDocument) {
      return null;
    }
    const date = dateFormatterDayMonthYearLong2Digits(
      new Date(supportingDocument.supportingDocument.createdAt),
    );
    const createdBy = supportingDocument.supportingDocument.createdBy;
    if (!createdBy) {
      return date;
    }
    return t(
      "home_charging_supporting_documents.sidePanel.detail.createdAtValue",
      {
        date,
        author: `${createdBy.firstName} ${createdBy.lastName.charAt(0)}.`,
      },
    );
  }, [supportingDocument.supportingDocument, t]);

  const excludedChargesValue = useMemo(() => {
    const excludedCharges = supportingDocument.charges.filter(
      (charge) => charge.excludedAt !== null,
    );
    if (excludedCharges.length === 0) {
      return null;
    }
    return {
      count: excludedCharges.length,
      amount: formatCurrency(
        excludedCharges.reduce(
          (sum, charge) => sum + (charge.reimbursementAmount ?? 0),
          0,
        ),
      ),
    };
  }, [supportingDocument.charges]);

  return (
    <>
      <SectionTitle>
        <TextCapitalized>
          {t("home_charging_supporting_documents.sidePanel.detail.title")}
        </TextCapitalized>
      </SectionTitle>
      <Spacer y={2} />
      <SidePanelDS.SectionRow
        label={
          <SectionRowLabel>
            <TextCapitalized>
              {t(
                "home_charging_supporting_documents.sidePanel.detail.chargesCount",
                { count: supportingDocument.chargesCount },
              )}
            </TextCapitalized>
          </SectionRowLabel>
        }
        value={formatCurrency(supportingDocument.reimbursementAmount)}
      />
      <Spacer y={2} />
      <SidePanelDS.SectionRow
        label={
          <SectionRowLabel>
            <TextCapitalized>
              {t(
                "home_charging_supporting_documents.sidePanel.detail.totalEnergy",
              )}
            </TextCapitalized>
          </SectionRowLabel>
        }
        value={`${supportingDocument.totalEnergy} kWh`}
      />
      {excludedChargesValue && (
        <>
          <Spacer y={2} />
          <SidePanelDS.SectionRow
            label={
              <SectionRowLabel>
                <TextCapitalized>
                  {t(
                    "home_charging_supporting_documents.sidePanel.detail.excludedCharges",
                    { count: excludedChargesValue.count },
                  )}
                </TextCapitalized>
              </SectionRowLabel>
            }
            value={excludedChargesValue.amount}
          />
        </>
      )}
      {createdAtValue && (
        <>
          <Spacer y={2} />
          <SidePanelDS.SectionRow
            label={
              <SectionRowLabel>
                <TextCapitalized>
                  {t(
                    "home_charging_supporting_documents.sidePanel.detail.createdAt",
                  )}
                </TextCapitalized>
              </SectionRowLabel>
            }
            value={createdAtValue}
          />
        </>
      )}
    </>
  );
};

const SectionTitle = styled.div`
  ${typographies["Body/L"]}
  color: ${colors["colors/text/darkGrey"]}
`;

const SectionRowLabel = styled.div`
  ${typographies["Body/L"]}
  color: ${colors["colors/text/black"]}
`;
